import { memo, useMemo } from 'react';
import { Empty, Skeleton } from 'antd';
import { Panel } from './Panel';
import { useWidgetData } from './shared';
import type { MatrisConfig, MatrisDashboardWidget } from '../../types/panelConfig';

interface Props {
  widget: MatrisDashboardWidget;
  sourceId: number | null | undefined;
  parameters?: Record<string, string>;
}

type Ton = 'kirmizi' | 'sari' | 'notr';
type Row = Record<string, unknown>;

const TON_STIL: Record<Ton, { background: string; color: string }> = {
  kirmizi: { background: 'var(--color-error-bg)', color: 'var(--color-error)' },
  sari: { background: 'color-mix(in srgb, var(--color-warning) 16%, transparent)', color: 'var(--color-warning)' },
  notr: { background: 'var(--color-hover-bg)', color: 'var(--color-text-primary)' },
};

function metin(v: unknown): string {
  return v === null || v === undefined ? '' : String(v);
}

export function hucreTonu(deger: unknown, esik: MatrisConfig['esik']): Ton {
  if (!esik || deger === null || deger === undefined || deger === '') return 'notr';
  const n = Number(deger);
  if (Number.isNaN(n)) return 'notr';
  if (esik.kirmizi !== undefined && n < esik.kirmizi) return 'kirmizi';
  if (esik.sari !== undefined && n < esik.sari) return 'sari';
  return 'notr';
}

function MatrisWidgetImpl({ widget, sourceId, parameters }: Props) {
  const cfg = widget.config;
  const { data, isLoading } = useWidgetData(sourceId, `matris-${widget.reportCode}`, parameters);

  // Eksenler verideki ilk görülme sırasıyla; hücre (satır, kolon) anahtarıyla bulunur.
  const { satirlar, kolonlar, hucre, satirAlt, kolonAlt } = useMemo(() => {
    const rows: Row[] = data ?? [];
    const satirlar: string[] = [];
    const kolonlar: string[] = [];
    const hucre = new Map<string, Row>();
    const satirAlt = new Map<string, string>();
    const kolonAlt = new Map<string, string>();
    for (const r of rows) {
      const s = metin(r[cfg.satirField]);
      const k = metin(r[cfg.kolonField]);
      if (!s || !k) continue;
      if (!satirAlt.has(s)) { satirlar.push(s); satirAlt.set(s, cfg.satirAltField ? metin(r[cfg.satirAltField]) : ''); }
      if (!kolonAlt.has(k)) { kolonlar.push(k); kolonAlt.set(k, cfg.kolonAltField ? metin(r[cfg.kolonAltField]) : ''); }
      hucre.set(`${s}\u0000${k}`, r);
    }
    return { satirlar, kolonlar, hucre, satirAlt, kolonAlt };
  }, [data, cfg]);

  return (
    <div data-testid="matris-widget">
      <Panel title={cfg.title} subtitle={cfg.subtitle}>
        {isLoading ? (
          <Skeleton.Node active style={{ width: '100%', height: 240 }} />
        ) : satirlar.length === 0 ? (
          <Empty description="Veri yok" style={{ padding: 40 }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {cfg.not && <div className="text-xs text-muted">{cfg.not}</div>}
            {/* Dar ekranda matris kendi içinde yatay kayar; sayfa kaymaz. */}
            <div style={{ overflowX: 'auto' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `minmax(110px, 140px) repeat(${kolonlar.length}, minmax(112px, 1fr))`,
                  gap: 6,
                  minWidth: 140 + kolonlar.length * 118,
                }}
              >
                <span />
                {kolonlar.map((k) => (
                  <div key={k} data-testid="matris-kolon" style={{ padding: '4px 6px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.25 }}>{k}</span>
                    {kolonAlt.get(k) && <span className="text-muted" style={{ fontSize: 11 }}>{kolonAlt.get(k)}</span>}
                  </div>
                ))}
                {satirlar.map((s) => (
                  <div key={s} style={{ display: 'contents' }}>
                    <div data-testid="matris-satir" style={{ padding: '4px 6px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{s}</span>
                      {satirAlt.get(s) && <span className="text-muted" style={{ fontSize: 11 }}>{satirAlt.get(s)}</span>}
                    </div>
                    {kolonlar.map((k) => {
                      const r = hucre.get(`${s}\u0000${k}`);
                      if (!r) return <div key={k} style={{ ...TON_STIL.notr, borderRadius: 6, opacity: 0.5 }} />;
                      const ton = hucreTonu(cfg.tonField ? r[cfg.tonField] : undefined, cfg.esik);
                      const ek = cfg.ekField ? metin(r[cfg.ekField]) : '';
                      return (
                        <div
                          key={k}
                          data-testid="matris-hucre"
                          data-ton={ton}
                          style={{ ...TON_STIL[ton], borderRadius: 6, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 2, minHeight: 60 }}
                        >
                          <span className="tabular-nums" style={{ fontSize: 16, fontWeight: 600 }}>{metin(r[cfg.hucreField])}</span>
                          {cfg.altField && (
                            <span className="tabular-nums" style={{ fontSize: 11, opacity: 0.8 }}>{metin(r[cfg.altField])}</span>
                          )}
                          {ek && <span style={{ fontSize: 12, fontWeight: 600 }}>{ek}</span>}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}

export const MatrisWidget = memo(MatrisWidgetImpl);
